import React from 'react';
import Modal from '../Modal';
import Input from '../Input';
import Button from '../Button';
import Action from '../Action';
import CheckCircleIcon from '../CheckCircleIcon';
import CopyIcon from '../CopyIcon';
import ShareIcon from '../ShareIcon';
import sleep from '../../util/sleep';
import { getIsLeagueNameTaken } from '../../resources/League/services';
import { createLeagueMutation, setLeagueStrictModeMutation } from '../../resources/League/mutations';
import CurrentUserResource from '../../resources/Auth/CurrentUser';
import { useAsyncEffect } from 'hooks';
import { makeLink } from '../../util/makeLink';
import useCopy from '../../util/useCopy';
import useShare from '../../util/useShare';
import copyToClipboard from 'copy-to-clipboard';
import { ScoringModeInput } from '../ScoringModeInput';
import { LEAGUE_NAME_MAX_LENGTH } from '../../config/constants';
import QrCodeIcon from '../QrCodeIcon';
import QrCodeModal from '../QrCodeModal';

const CreateLeagueModal = ({ isOpen, onClose }) => {
  const copy = useCopy();
  const [currentUser] = CurrentUserResource.useLoadable();

  const [nameText, setNameText] = React.useState('');
  const [isNameTaken, setIsNameTaken] = React.useState();
  const [strictMode, setStrictMode] = React.useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = React.useState(false);

  useAsyncEffect(
    async getIsCurrent => {
      setIsNameTaken();
      await sleep(300);
      if (!getIsCurrent()) return;
      const isNameTaken = await getIsLeagueNameTaken(nameText);
      if (!getIsCurrent()) return;
      setIsNameTaken(isNameTaken);
    },
    [nameText, setIsNameTaken],
  );

  const canSaveName = Boolean(nameText) && isNameTaken === false;

  const [isSaving, setIsSaving] = React.useState(false);
  const [didSaveData, setDidSaveData] = React.useState(null);
  const { share, isSharing, isSupported: isShareSupported } = useShare();

  const onSave = React.useCallback(async () => {
    if (!canSaveName) return;

    setIsSaving(true);

    const [didSaveData] = await Promise.all([
      await createLeagueMutation({ userId: currentUser.id, name: nameText }),
      sleep(2000),
    ]);

    setIsSaving(false);
    if (!didSaveData) return;
    setDidSaveData(didSaveData);
    setNameText('');
  }, [nameText, canSaveName, currentUser.id]);

  const handleClose = React.useCallback(() => {
    onClose();
    setIsSaving(false);
    setDidSaveData(null);
    setNameText('');
    setStrictMode(false);
    setShowQrCodeModal(false);
  }, [onClose]);

  const modalContent = React.useMemo(() => {
    if (didSaveData)
      return (
        <>
          <Modal.MainText>
            <p>
              <strong>{didSaveData.name}</strong> {copy.isReadyToGo}!
            </p>
          </Modal.MainText>
          <Modal.BottomActions>
            <ScoringModeInput
              stacked
              value={strictMode}
              onChange={(newStrictMode) => {
                setStrictMode(newStrictMode);
                setLeagueStrictModeMutation({
                  leagueId: didSaveData.leagueId,
                  userId: currentUser.id,
                  strictMode: newStrictMode,
                });
              }}
            />
            <div className="mb-3 flex">
              {isShareSupported ? (
                <Button
                  className="flex-1 rounded-r-none"
                  disabled={isSharing}
                  isLoading={isSharing}
                  onClick={() =>
                    share({
                      title: copy.joinMyFantasyOscarsLeague,
                      url: makeLink(didSaveData.joinLinkId),
                    })
                  }
                  Icon={ShareIcon}
                >
                  {copy.invite}
                </Button>
              ) : (
                <Button
                  className="flex-1 rounded-r-none"
                  onClick={() => {
                    copyToClipboard(makeLink(didSaveData.joinLinkId));
                  }}
                  Icon={CopyIcon}
                >
                  {copy.invite}
                </Button>
              )}
              <Button
                className="flex-none rounded-l-none border-l-0"
                Icon={QrCodeIcon}
                onClick={() => setShowQrCodeModal(true)}
              />
            </div>
            <Action onClick={handleClose}>{copy.finish}</Action>
          </Modal.BottomActions>
        </>
      );

    if (isSaving) return <Modal.Loader />;

    return (
      <>
        <Modal.MainText>
          <p>{copy.enterANameForYourLeague}.</p>
        </Modal.MainText>
        <Modal.BottomActions>
          <p>{isNameTaken ? copy.shootThatOneSAlreadyTaken : <>&ensp;</>}</p>
          <Input stacked value={nameText} onChange={(v) => setNameText(v.slice(0, LEAGUE_NAME_MAX_LENGTH))} />
          <Button
            stacked
            disabled={!canSaveName}
            onClick={() => {
              onSave(nameText);
            }}
            Icon={CheckCircleIcon}
          >
            {copy.createLeague}
          </Button>
        </Modal.BottomActions>
      </>
    );
  }, [
    copy,
    onSave,
    isNameTaken,
    setNameText,
    nameText,
    canSaveName,
    isSaving,
    isSharing,
    isShareSupported,
    share,
    didSaveData,
    handleClose,
    strictMode,
    currentUser.id,
  ]);

  return (
    <>
      {didSaveData?.joinLinkId && (
        <QrCodeModal
          isOpen={showQrCodeModal}
          onClose={() => setShowQrCodeModal(false)}
          url={makeLink(didSaveData.joinLinkId)}
          leagueName={didSaveData.name}
        />
      )}
      <Modal isOpen={isOpen} onClose={handleClose} className="CreateLeagueModal">
        {modalContent}
      </Modal>
    </>
  );
};

export default CreateLeagueModal;
